/**
 * Semantic icon barrel. The app standardizes on react-icons, drawn from a
 * single coherent family (`react-icons/lu`, Lucide) for visual consistency.
 * Call sites import stable semantic names from here so the underlying family
 * can be swapped in one place; reach into other react-icons packs only when
 * the `lu` set genuinely lacks a glyph.
 *
 * Every icon accepts a numeric `size` (px) prop, matching the previous
 * hand-rolled SVG API so existing call sites work unchanged.
 */
import type { IconBaseProps } from "react-icons";
import {
  LuArrowRight,
  LuBookOpen,
  LuBookUp,
  LuChevronLeft,
  LuChevronRight,
  LuDrama,
  LuExternalLink,
  LuInbox,
  LuHeadphones,
  LuKanban,
  LuLayoutGrid,
  LuLibrary,
  LuMapPin,
  LuPencil,
  LuPlay,
  LuPlus,
  LuSave,
  LuSettings,
  LuSparkles,
  LuTrash2,
  LuUser,
  LuUsers,
  LuWaypoints,
  LuX,
} from "react-icons/lu";
import type { IconType } from "react-icons";

/** Wrap a react-icons component so it takes our `{ size }` prop (px number). */
function makeIcon(Icon: IconType, defaultSize = 20) {
  return function WorkbenchIcon({
    size = defaultSize,
    ...props
  }: { size?: number } & Omit<IconBaseProps, "size">) {
    return <Icon size={size} aria-hidden {...props} />;
  };
}

// —— Existing named exports (kept stable for current call sites) ——
export const ChevronLeftIcon = makeIcon(LuChevronLeft, 16);
export const ChevronRightIcon = makeIcon(LuChevronRight, 16);
export const IconBookStack = makeIcon(LuBookOpen);
export const IconUser = makeIcon(LuUser);
export const IconUsers = makeIcon(LuUsers);
export const IconMapPin = makeIcon(LuMapPin);
export const IconTimeline = makeIcon(LuWaypoints);
export const IconThemes = makeIcon(LuDrama);
export const IconSparkles = makeIcon(LuSparkles);
export const IconPencil = makeIcon(LuPencil);
export const IconX = makeIcon(LuX);
export const IconSave = makeIcon(LuSave);
export const IconSettings = makeIcon(LuSettings);
export const IconPublish = makeIcon(LuBookUp);
export const IconPlay = makeIcon(LuPlay);
export const IconLayoutGrid = makeIcon(LuLayoutGrid);
export const IconLibrary = makeIcon(LuLibrary);
export const IconKanban = makeIcon(LuKanban);
export const IconInbox = makeIcon(LuInbox);
export const IconAudio = makeIcon(LuHeadphones);

// —— New semantic icons for text→icon conversions ——
export const IconRemove = makeIcon(LuTrash2, 16);
export const IconAdd = makeIcon(LuPlus, 16);
export const IconOpenIn = makeIcon(LuExternalLink, 16);
export const IconArrowRight = makeIcon(LuArrowRight, 16);
